import { useTranslation } from "react-i18next"
import { Button, Alert, Heading, BadgeVariation } from "@oysterhr/ui"
import { staffOffboardingFlowPath } from "@oysterhr/router"
import { useNavigate } from "react-router-dom"
import CancelButton from "../TabTermination/StateTransitions/Cancel"
import { MarkAsCompletedButton } from "../TabTermination/StateTransitions/MarkAsCompleted/MarkAsCompletedButton"
import { TerminationState } from "@oysterhr/schema"
import { PillField } from "~/adminApp/pages/EmploymentPage/infoFields"
import AproveCancellationButton from "../TabTermination/StateTransitions/ApproveCancellation"
import PreviousRequestsSection from "../TabTermination/PreviousRequestsSection"
import { StaffOffboardingSummary } from "../OffboardingSummary/StaffOffboardingSummary"
import { useEngagement } from "../../hooks/useOffboardingFlowEngagement"
interface GlobalPayrollTabTerminationProps {
  engagementId: string
  companyId: string
}

const statePillConfig: { [key: string]: { text: string; variation: BadgeVariation } } = {
  [TerminationState.Initiated]: {
    text: "Initiated",
    variation: "info",
  },
  [TerminationState.Terminated]: {
    text: "Completed",
    variation: "success",
  },
  [TerminationState.CancellationRequested]: {
    text: "Cancellation Requested",
    variation: "warning",
  },
}

export const GlobalPayrollTabTermination = ({
  engagementId,
  companyId,
}: GlobalPayrollTabTerminationProps) => {
  const { t } = useTranslation("staff_interface/GlobalPayrollTabTermination")
  const navigate = useNavigate()
  const navigateToOffboardingFlow = () => {
    navigate(staffOffboardingFlowPath(companyId, engagementId))
  }
  const { data, loading, refetch } = useEngagement(engagementId)

  if (loading) {
    return null
  }
  const engagement = data?.engagement
  const latestTermination = engagement?.termination
  const pillConfig = statePillConfig[latestTermination?.state]
  const previousTerminations = engagement?.cancelledTerminations || []

  const cancellationRequested = latestTermination?.state === TerminationState.CancellationRequested

  const analyticsName = () =>
    engagement?.terminationDraft
      ? "offboarding-tab_continue-gp-offboarding-flow"
      : "offboarding-tab_start-gp-offboarding-flow"

  return (
    <div className="mt-8 pr-4">
      {cancellationRequested && (
        <Alert className="mb-4" variation="warning">
          <Alert.Icon />
          <Alert.Content>{t("cancellationRequested")}</Alert.Content>
        </Alert>
      )}
      <div className="flex w-full justify-between">
        {latestTermination && (
          <PillField label="Status" value={pillConfig.text} variation={pillConfig.variation} />
        )}
        <div className="flex h-10">
          <AproveCancellationButton
            engagement={engagement}
            refetch={refetch}
            latestTermination={latestTermination}
            isContract={false}
          />
          <CancelButton
            engagement={engagement}
            refetch={refetch}
            latestTermination={latestTermination}
          />
          <MarkAsCompletedButton
            engagement={engagement}
            refetch={refetch}
            latestTermination={latestTermination}
          />
        </div>
      </div>
      {latestTermination && (
        <div className="text-sm md:text-base">
          <StaffOffboardingSummary engagement={engagement} />
        </div>
      )}
      {!latestTermination && (
        <Button
          className="mb-5 mt-5"
          data-analytics-name={analyticsName()}
          onClick={navigateToOffboardingFlow}
        >
          {engagement?.terminationDraft ? t("continue") : t("start")}
        </Button>
      )}
      {!!previousTerminations.length && (
        <>
          <Heading as="h3" className="mb-8">
            {t("previousRequests")}
          </Heading>
          <PreviousRequestsSection terminations={previousTerminations} />
        </>
      )}
    </div>
  )
}
